import { Box, Button, Container, Field, Heading, Input } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useColorModeValue } from "../UI/color-mode"

import { type ApiError, type UpdatePassword, UsersService } from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import { confirmPasswordRules, handleError, passwordRules } from "../../utils"

interface UpdatePasswordForm extends UpdatePassword {
    confirm_password: string
}

const ChangePassword = () => {
  const color = useColorModeValue("inherit", "light")
  const showToast = useCustomToast()

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UpdatePasswordForm>({
    mode: "onBlur",
    criteriaMode: "all",
  })

  const mutation = useMutation({
    mutationFn: (data: UpdatePassword) =>
      UsersService.updatePasswordMe({ requestBody: data }),
    onSuccess: () => {
      showToast("Aktion erfolgreich!", "Passwort wurde geändert", "success")
      reset()
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<UpdatePasswordForm> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <Container maxW="full">
      <Heading size="sm" py={4}>
        Passwort ändern
      </Heading>
      <Box
        w={{ sm: "full", md: "50%" }}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Field.Root required invalid={!!errors.current_password}>
          <Field.Label color={color} htmlFor="current_password">
            Aktuelles Passwort
            <Field.RequiredIndicator />
          </Field.Label>
          <Input
            id="current_password"
            {...register("current_password")}
            placeholder="Passwort"
            type="password"
            w="auto"
          />
          {errors.current_password && (
            <Field.ErrorText>
              {errors.current_password.message}
            </Field.ErrorText>
          )}
        </Field.Root>
        <Field.Root mt={4} required invalid={!!errors.new_password}>
          <Field.Label htmlFor="password">
            Neues Passwort
            <Field.RequiredIndicator />
          </Field.Label>
          <Input
            id="password"
            {...register("new_password", passwordRules())}
            placeholder="Passwort"
            type="password"
            w="auto"
          />
          {errors.new_password && (
            <Field.ErrorText>{errors.new_password.message}</Field.ErrorText>
          )}
        </Field.Root>
        <Field.Root mt={4} required invalid={!!errors.confirm_password}>
          <Field.Label htmlFor="confirm_password">
            Passwort bestätigen
            <Field.RequiredIndicator />
          </Field.Label>
          <Input
            id="confirm_password"
            {...register("confirm_password", confirmPasswordRules(getValues))}
            placeholder="Passwort"
            type="password"
            w="auto"
          />
          {errors.confirm_password && (
            <Field.ErrorText>
              {errors.confirm_password.message}
            </Field.ErrorText>
          )}
        </Field.Root>
        <Button
          variant="primary"
          mt={4}
          type="submit"
          loading={isSubmitting}
        >
          Speichern
        </Button>
      </Box>
    </Container>
  )
}
export default ChangePassword
